import React, { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import useStats from '../hooks/useStats';
import { registerGlobalRefresh, fixFavoriteDataConsistency, debugFavoriteData, clearAllFavorites, testMovieFavoriteStatus, isFavoriteSimple, resetFavoriteState, stopGlobalRefresh, resetGlobalRefreshState } from '../services/favoriteService';
import { Star, Calendar, Film, Sparkles, Heart, Eye } from 'lucide-react';

const Dashboard = ({ setActiveTab }) => {
    const { user, profile } = useAuth();
    const { t, formatDate } = useLanguage();
    // Görev 4: İstatistik hook'u kullan
    const stats = useStats();

    // Global refresh sistemini kaydet
    useEffect(() => {
        const unregister = registerGlobalRefresh(() => {
            // Dashboard için istatistikleri yenile
            stats.refresh();
        });

        return unregister; // Cleanup
    }, [stats]);

    // Debug komutları console'da kullanılabilir hale getir (sadece geliştirme için)
    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            window.fixFavorites = fixFavoriteDataConsistency;
            window.debugFavorites = debugFavoriteData;
            window.clearFavorites = clearAllFavorites;
            window.testMovie = testMovieFavoriteStatus;
            window.isFavoriteSimple = isFavoriteSimple;
            window.resetFavorites = resetFavoriteState;
            window.stopRefresh = stopGlobalRefresh;
            window.resetRefresh = resetGlobalRefreshState;
        }
    }, []);

    // Film açıklamasını parse etme fonksiyonu
    const parseMovieDescription = (description) => {
        if (!description) return {};

        const lines = description.split('\n');
        const parsed = {};

        lines.forEach(line => {
            if (line.includes('🎬 Film Adı:')) {
                parsed.title = line.replace('🎬 Film Adı:', '').trim();
            } else if (line.includes('📆 Yıl:')) {
                parsed.year = line.replace('📆 Yıl:', '').trim();
            } else if (line.includes('🌟 IMDb Puanı:')) {
                parsed.rating = line.replace('🌟 IMDb Puanı:', '').trim();
            } else if (line.includes('📝 Kısa Açıklama:')) {
                parsed.explanation = line.replace('📝 Kısa Açıklama:', '').trim();
            } else if (line.includes('🖼️ Poster URL:')) {
                parsed.posterUrl = line.replace('🖼️ Poster URL:', '').trim();
            }
        });

        return parsed;
    };

    // Görev 5: Skeleton loader komponenti
    const StatSkeleton = ({ name, icon: Icon, color, bgColor }) => (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
                <div className={`${bgColor} rounded-lg p-3`}>
                    <Icon className={`h-6 w-6 ${color}`} />
                </div>
                <div className="ml-4 flex-1">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2 animate-pulse"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-12 animate-pulse"></div>
                </div>
            </div>
        </div>
    );

    const statsCards = [
        {
            name: t('dashboard.totalRecommendations'),
            value: stats.totalRecommendations,
            icon: Sparkles,
            color: 'text-blue-600 dark:text-blue-400',
            bgColor: 'bg-blue-100 dark:bg-blue-900/50'
        },
        {
            name: t('dashboard.favorites'),
            value: stats.totalFavorites,
            icon: Heart,
            color: 'text-red-600 dark:text-red-400',
            bgColor: 'bg-red-100 dark:bg-red-900/50'
        },
        {
            name: t('dashboard.watchedThisMonth'),
            value: stats.watchedThisMonth,
            icon: Eye,
            color: 'text-green-600 dark:text-green-400',
            bgColor: 'bg-green-100 dark:bg-green-900/50'
        },
        {
            name: t('dashboard.totalWatched'),
            value: stats.totalWatched || 0,
            icon: Calendar,
            color: 'text-purple-600 dark:text-purple-400',
            bgColor: 'bg-purple-100 dark:bg-purple-900/50'
        }
    ];

    return (
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
            <div className="text-center mb-6 sm:mb-8">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    <Film className="inline h-6 w-6 sm:h-8 sm:w-8 text-purple-600 mr-2" />
                    {t('dashboard.welcome')}, {profile?.name || user?.user_metadata?.name || user?.email?.split('@')[0] || t('common.user')}!
                </h1>
                <p className="text-gray-600 dark:text-gray-400 px-4">
                    {t('dashboard.subtitle')}
                </p>
            </div>

            {/* İstatistik Kartları - Görev 5: Yükleme koruması */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {stats.loading ? (
                    // Skeleton loader göster
                    statsCards.map((stat) => (
                        <StatSkeleton key={stat.name} {...stat} />
                    ))
                ) : (
                    // Gerçek verileri göster
                    statsCards.map((stat) => {
                        const Icon = stat.icon;
                        return (
                            <div key={stat.name} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                <div className="flex items-center">
                                    <div className={`${stat.bgColor} rounded-lg p-3`}>
                                        <Icon className={`h-6 w-6 ${stat.color}`} />
                                    </div>
                                    <div className="ml-4">
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.name}</p>
                                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Hızlı Eylemler */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow p-6 mb-8">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-white mb-4">
                        {t('dashboard.readyForRecommendations')}
                    </h2>
                    <p className="text-indigo-100 mb-6">
                        {t('dashboard.recommendationsSubtitle')}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={() => setActiveTab('recommend')}
                            className="bg-white text-indigo-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                        >
                            {t('recommendation.getRecommendation')}
                        </button>
                        <button
                            onClick={() => setActiveTab('history')}
                            className="bg-indigo-400 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-300 transition-colors"
                        >
                            {t('dashboard.viewHistory')}
                        </button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-8">
                {/* Son Öneriler */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center space-x-2">
                            <Sparkles className="h-5 w-5 text-purple-600" />
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {t('dashboard.recentRecommendations')}
                            </h3>
                        </div>
                    </div>
                    <div className="p-6">
                        {stats.loading ? (
                            // Skeleton loader
                            <div className="space-y-4">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                        <div className="flex items-center space-x-4">
                                            <div className="w-12 h-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                                            <div className="flex-1">
                                                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2 animate-pulse"></div>
                                                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-1 animate-pulse"></div>
                                                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20 animate-pulse"></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : stats.recentRecommendations.length > 0 ? (
                            <div className="space-y-4">
                                {stats.recentRecommendations.map((movie, index) => {
                                    const parsedInfo = parseMovieDescription(movie.description);
                                    return (
                                        <div key={movie.id || index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                            <div className="flex items-center space-x-4">
                                                <div className="w-12 h-16 bg-gradient-to-br from-purple-500 via-indigo-600 to-blue-700 rounded flex-shrink-0 overflow-hidden">
                                                    {(movie.posterUrl || parsedInfo.posterUrl) ? (
                                                        <img
                                                            src={movie.posterUrl || parsedInfo.posterUrl}
                                                            alt={movie.title}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-white">
                                                            <div className="text-center p-1">
                                                                <Film className="h-4 w-4 mx-auto mb-1 opacity-90" />
                                                                <div className="text-xs font-bold leading-tight">{movie.title.substring(0, 8)}</div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                                        {movie.title}
                                                    </h4>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                        {movie.year && `${movie.year} • `}{movie.genre}
                                                    </p>
                                                    {movie.mood && (
                                                        <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                                                            {t('dashboard.mood')}: {movie.mood}
                                                        </p>
                                                    )}
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                        {formatDate(movie.recommendedAt)}
                                                    </p>
                                                </div>
                                                {movie.rating && (
                                                    <div className="flex items-center space-x-1">
                                                        <Star className="h-4 w-4 text-yellow-400 fill-current" />
                                                        <span className="text-sm text-gray-600 dark:text-gray-400">{movie.rating}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                    <div className="text-center">
                                        <button
                                            onClick={() => setActiveTab('history')}
                                            className="text-purple-600 hover:text-purple-500 dark:text-purple-400 dark:hover:text-purple-300 font-medium transition-colors"
                                        >
                                            {t('dashboard.viewAll')} →
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <Sparkles className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 dark:text-gray-400 mb-4">
                                    {t('dashboard.noRecommendations')}
                                </p>
                                <button
                                    onClick={() => setActiveTab('recommend')}
                                    className="text-indigo-600 hover:text-indigo-500 font-medium"
                                >
                                    {t('dashboard.getFirstRecommendation')} →
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* En Çok Sevilen Türler */}
                {!stats.loading && stats.favoriteGenres && stats.favoriteGenres.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center space-x-2">
                                <Film className="h-5 w-5 text-purple-600" />
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {t('dashboard.favoriteGenres')}
                                </h3>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="space-y-4">
                                {stats.favoriteGenres.map((item, index) => (
                                    <div key={item.genre} className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${index === 0 ? 'bg-yellow-500' :
                                                index === 1 ? 'bg-gray-400' :
                                                    'bg-orange-400'
                                                }`}>
                                                {index + 1}
                                            </div>
                                            <span className="text-gray-700 dark:text-gray-300 font-medium">
                                                {item.genre}
                                            </span>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            <div className="w-24 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                                <div
                                                    className={`h-2 rounded-full ${index === 0 ? 'bg-yellow-500' :
                                                        index === 1 ? 'bg-gray-400' :
                                                            'bg-orange-400'
                                                        }`}
                                                    style={{
                                                        width: `${stats.totalFavorites > 0 ? (item.count / stats.totalFavorites) * 100 : 0}%`
                                                    }}
                                                ></div>
                                            </div>
                                            <span className="text-sm text-gray-600 dark:text-gray-400 font-medium min-w-[2rem] text-right">
                                                {item.count}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {stats.totalFavorites > 0 && (
                                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                                    <div className="text-center">
                                        <button
                                            onClick={() => setActiveTab('favorites')}
                                            className="text-purple-600 hover:text-purple-500 dark:text-purple-400 dark:hover:text-purple-300 font-medium transition-colors"
                                        >
                                            {t('dashboard.viewAllFavorites')} →
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dashboard; 